import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth/services/auth.service';
import {
  FirmwareFile,
  StartUpdateResponse,
  DeviceFilters,
  DevicesResponse,
  CompanyOption,
  QueueDevice,
  DeviceOptions,
  FirmwareSelection,
} from '../models/firmware-update.model';

@Injectable({
  providedIn: 'root',
})
export class FirmwareUpdateService {
  private readonly baseUrl = `${environment.webappEndpoint}/firmware`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) { }

  private getHeaders() {
    return { Authorization: `Bearer ${this.authService.getToken()}` };
  }

  startUpdate(selections: FirmwareSelection[], force: boolean = false): Observable<StartUpdateResponse> {
    return this.http.post<StartUpdateResponse>(
      `${this.baseUrl}/update`,
      { selections, force },
      { headers: this.getHeaders() }
    );
  }

  getDeviceOptions(macs: string[]): Observable<{ success: boolean; data: DeviceOptions[] }> {
    return this.http.post<{ success: boolean; data: DeviceOptions[] }>(
      `${this.baseUrl}/device-options`,
      { macs },
      { headers: this.getHeaders() }
    );
  }

  getGlobalQueue(): Observable<{ success: boolean; data: QueueDevice[] }> {
    return this.http.get<{ success: boolean; data: QueueDevice[] }>(
      `${this.baseUrl}/queue`,
      { headers: this.getHeaders() }
    );
  }

  acknowledgeGlobalQueue(): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.baseUrl}/queue/acknowledge`,
      {},
      { headers: this.getHeaders() }
    );
  }

  cancelDevices(macs: string[]): Observable<{ success: boolean; cancelled?: string[] }> {
    return this.http.post<{ success: boolean; cancelled?: string[] }>(
      `${this.baseUrl}/cancel`,
      { macs },
      { headers: this.getHeaders() }
    );
  }

  retryDevice(mac: string): Observable<{ success: boolean; reason?: string }> {
    return this.http.post<{ success: boolean; reason?: string }>(
      `${this.baseUrl}/retry/${mac}`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getAvailableFirmware(): Observable<{ success: boolean; files: FirmwareFile[] }> {
    return this.http.get<{ success: boolean; files: FirmwareFile[] }>(
      `${this.baseUrl}/available`,
      { headers: this.getHeaders() }
    );
  }

  getDevices(filters: DeviceFilters): Observable<DevicesResponse> {
    let params = new HttpParams()
      .set('page', filters.page.toString())
      .set('limit', filters.limit.toString());
    if (filters.company) params = params.set('company', filters.company);
    if (filters.apiKey) params = params.set('apiKey', filters.apiKey);
    if (filters.mac) params = params.set('mac', filters.mac);

    return this.http.get<DevicesResponse>(
      `${this.baseUrl}/devices`,
      { headers: this.getHeaders(), params }
    );
  }

  getCompanies(): Observable<{ success: boolean; data: CompanyOption[] }> {
    return this.http.get<{ success: boolean; data: CompanyOption[] }>(
      `${this.baseUrl}/companies`,
      { headers: this.getHeaders() }
    );
  }

  getApiKeys(companyId?: string): Observable<{ success: boolean; data: string[] }> {
    let params = new HttpParams();
    if (companyId) params = params.set('company', companyId);
    return this.http.get<{ success: boolean; data: string[] }>(
      `${this.baseUrl}/apikeys`,
      { headers: this.getHeaders(), params }
    );
  }

  getRecentHistory(days: number = 15): Observable<{ success: boolean; data: any[] }> {
    const params = new HttpParams().set('days', days.toString());
    return this.http.get<{ success: boolean; data: any[] }>(
      `${this.baseUrl}/history`,
      { headers: this.getHeaders(), params }
    );
  }

  getReceiverConfig(apiKey: string): Observable<{ success: boolean; data: any }> {
    return this.http.get<{ success: boolean; data: any }>(
      `${this.baseUrl}/receiver-config/${apiKey}`,
      { headers: this.getHeaders() }
    );
  }

  updateReceiverConfig(apiKey: string, config: any): Observable<{ success: boolean; data: any }> {
    return this.http.put<{ success: boolean; data: any }>(
      `${this.baseUrl}/receiver-config/${apiKey}`,
      config,
      { headers: this.getHeaders() }
    );
  }
}
